<script lang="ts">
  import type { Snippet } from 'svelte';
  import ArrowDown from '@lucide/svelte/icons/arrow-down';
  import MotionAction from '$lib/components/landing/effects/motion-action.svelte';

  let {
    eyebrow,
    title,
    emphasis,
    lead,
    secondary,
    note,
    visual
  }: {
    eyebrow: string;
    title: string;
    emphasis: string;
    lead: string;
    secondary?: { label: string; href: string };
    note?: string;
    visual?: Snippet;
  } = $props();
  const emphasisWords = $derived(emphasis.split(' '));
</script>

<section class="page-hero" class:has-visual={visual}>
  <div class="page-hero-glow" aria-hidden="true"></div>
  <div class="page-hero-copy">
    <h1>
      <span class="eyebrow hero-eyebrow" data-hero-fade>{eyebrow}</span>
      <span class="hero-title-line">{title}</span>
      <span class="hero-title-line hero-serif"
        >{#each emphasisWords as word, index}<span class="hero-ink">{word}</span
          >{#if index < emphasisWords.length - 1}{' '}{/if}{/each}</span
      >
    </h1>
    <p data-hero-fade>{lead}</p>
    <div class="page-hero-actions" data-hero-fade>
      <MotionAction />
      {#if secondary}<a class="secondary-link" href={secondary.href}>{secondary.label} <ArrowDown size={15} /></a>{/if}
    </div>
    {#if note}<p class="page-hero-note" data-hero-fade>{note}</p>{/if}
  </div>
  {#if visual}
    <div class="page-hero-visual">
      <div class="page-hero-arc" aria-hidden="true" data-parallax="-30"></div>
      <div class="page-hero-stage" data-hero-visual>{@render visual()}</div>
    </div>
  {/if}
</section>

<style>
  .page-hero {
    position: relative;
    overflow: clip;
    padding-top: 92px;
    text-align: center;
  }
  .page-hero:not(.has-visual) {
    padding-bottom: 96px;
  }
  .page-hero-glow {
    position: absolute;
    inset: -120px 0 auto;
    height: 640px;
    background:
      radial-gradient(ellipse 50% 60% at 50% 0%, #dbe5ff, transparent 70%),
      linear-gradient(#1447e60d 1px, transparent 1px), linear-gradient(90deg, #1447e60d 1px, transparent 1px);
    background-size:
      100% 100%,
      56px 56px,
      56px 56px;
    mask-image: radial-gradient(ellipse 70% 70% at 50% 20%, #000 40%, transparent 85%);
    pointer-events: none;
  }
  .page-hero-copy {
    position: relative;
    z-index: 2;
    width: min(880px, calc(100% - 48px));
    margin-inline: auto;
  }
  h1 {
    margin: 0;
    font-size: clamp(52px, 6.4vw, 92px);
    font-weight: 500;
    line-height: 1.03;
    letter-spacing: -0.04em;
    text-wrap: balance;
  }
  h1 > span {
    display: block;
  }
  h1 > .hero-eyebrow {
    display: flex;
    width: fit-content;
    margin: 0 auto 22px;
    font-family: Geist, sans-serif;
    font-size: 12px;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: 0.01em;
    text-wrap: wrap;
  }
  .hero-serif {
    font-family: 'Instrument Serif', Georgia, serif;
    font-size: 1.32em;
    font-style: italic;
    font-weight: 400;
    letter-spacing: -0.035em;
    line-height: 0.98;
    padding-bottom: 0.08em;
  }
  .hero-ink {
    display: inline-block;
    padding-right: 0.025em;
    margin-right: -0.025em;
  }
  @supports (background-clip: text) {
    .hero-ink {
      background: linear-gradient(100deg, #1447e6 25%, #3c63e8 46%, #6d8dfa 50%, #3c63e8 54%, #1447e6 75%);
      background-size: 200% 100%;
      background-position: 50% 0;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      animation: page-ink-sweep 10s ease-in-out 1.8s infinite;
    }
  }
  @keyframes page-ink-sweep {
    0%,
    70%,
    100% {
      background-position: 50% 0;
    }
    82% {
      background-position: 75% 0;
    }
    92% {
      background-position: 25% 0;
    }
  }
  :global(html[data-motion='paused']) .hero-ink {
    animation: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .hero-ink {
      animation: none;
    }
  }
  @media (forced-colors: active) {
    .hero-ink {
      background: none;
      color: CanvasText;
      -webkit-text-fill-color: CanvasText;
    }
  }
  .page-hero-copy > p:not(.page-hero-note) {
    max-width: 560px;
    margin: 24px auto 0;
    color: #596b84;
    font-size: 17px;
    line-height: 1.65;
  }
  .page-hero-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 14px 25px;
    margin-top: 30px;
  }
  .secondary-link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding-block: 15px;
    font-size: 13px;
    transition: color 200ms;
  }
  .secondary-link:hover {
    color: #1447e6;
  }
  .page-hero-note {
    margin-top: 18px;
    color: #64758d;
    font-size: 11px;
  }
  .page-hero-visual {
    position: relative;
    margin-top: 64px;
    padding: 0 24px 72px;
    perspective: 1400px;
  }
  .page-hero-arc {
    position: absolute;
    left: -30%;
    width: 160%;
    top: 120px;
    bottom: -80px;
    border-radius: 50% 50% 0 0;
    background: radial-gradient(ellipse at 50% 0%, #5d83fc, transparent 60%), linear-gradient(180deg, #2354e9, #1b44c8);
  }
  .page-hero-stage {
    position: relative;
    z-index: 1;
    max-width: 1000px;
    margin-inline: auto;
    text-align: left;
  }
  @media (max-width: 760px) {
    .page-hero {
      padding-top: 56px;
    }
    .page-hero:not(.has-visual) {
      padding-bottom: 64px;
    }
    .page-hero-copy {
      width: calc(100% - 40px);
    }
    h1 {
      font-size: clamp(36px, 10.4vw, 60px);
    }
    .hero-serif {
      font-size: 1.2em;
    }
    .page-hero-copy > p:not(.page-hero-note) {
      font-size: 16px;
      max-width: 340px;
    }
    .page-hero-actions {
      gap: 8px 18px;
    }
    .page-hero-visual {
      margin-top: 44px;
      padding: 0 12px 44px;
    }
    .page-hero-arc {
      top: 80px;
      left: -60%;
      width: 220%;
    }
  }
</style>
